import { createFileRoute } from '@tanstack/react-router'
import { resolveSite, siteBrand } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { QATAR_BRAND } from '@/components/qatar/brand'
import { UmbrellaPrivacy } from '@/components/umbrella/Legal'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'
import { Link } from '@tanstack/react-router'

export const Route = createFileRoute('/privacy')({
  component: PrivacyRoute,
})

function PrivacyRoute() {
  return resolveSite() === 'umbrella' ? <UmbrellaPrivacy /> : <PrivacyPage />
}

function PrivacyPage() {
  const brand = siteBrand()
  const locale = useLocale()
  const t = useUi()
  const isQatar = resolveSite() === 'qatar'
  if (isQatar && locale === 'ar') return <PrivacyPageArabic />
  // On the bank face the "site" is the bank's program, not GranGuard.
  const siteName = isQatar ? QATAR_BRAND.program.en : brand.name
  const supportEmail = isQatar ? QATAR_BRAND.fraudEmail : brand.supportEmail
  const partner = COMMUNITY_PARTNER
  // Who operates the servers: GranGuard (The Guardian Project LLC) runs the
  // platform behind every face, including the bank-branded one.
  const operator = 'GranGuard'
  const link = 'font-medium text-violet-600 underline decoration-violet-300 underline-offset-2 hover:text-violet-700'
  return (
    <div className="bg-white">
      <div className="mx-auto max-w-3xl px-6 pb-20 pt-12">
        <p className="text-sm font-medium text-slate-500">{t.privacyEyebrow}</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {t.privacyTitle}
        </h1>
        <p className="mt-3 text-sm text-slate-400">
          {t.privacyUpdated}
        </p>

        <div className="mt-10 space-y-8 text-base leading-relaxed text-slate-700">
          <Section title="What we collect">
            <p>
              {siteName} is {isQatar ? 'an' : 'a free'} educational website. We do not require an
              account, do not run ads, and do not sell your personal
              information.
              {partner && (
                <> It is run by {operator} and offered to you by {partner.name}.</>
              )}
            </p>
            <p>
              We collect a small amount of standard usage data through Google
              Analytics so we can understand which pages and lessons are
              helpful and where the site can be improved. This data includes
              things like the pages you visit, how long you spend on each one,
              your approximate location (country, state and city, worked out
              from your internet connection), and the type of device and
              browser you’re using.
            </p>
            <p>
              We do not ask for names, addresses, phone numbers, or payment
              information. We do not use Google Analytics for advertising
              purposes.
            </p>
          </Section>

          <Section title="The Scam Checker">
            <p>
              When you use the Scam Checker, the text, links, pictures,
              recordings or videos you submit are sent to {operator}’s servers
              so we can check them. To do that we share them with service
              providers: an AI provider (OpenAI) reads the content, and links
              are checked against threat services such as Google Safe
              Browsing. For a link, we also look up public records about the
              website address, such as when it was registered.
            </p>
            <p>
              Uploaded pictures, recordings and videos are deleted once the
              check is finished. What you submit is never published, and we
              use it only to run the check.
            </p>
            <p>
              Please don’t paste passwords, full card numbers or account
              numbers. If a message contains them, remove them first. The
              check works just as well without them.
            </p>
          </Section>

          <Section title="The Password Check">
            <p>
              Your password never leaves your device. The strength check runs
              in your browser. For the breach check, your browser turns the
              password into a one-way fingerprint (a hash) and sends only the
              first five characters of it to the Have I Been Pwned service.
              The service sends back a list of matching fingerprints and your
              browser compares them, so neither we nor the service ever see
              your password.
            </p>
          </Section>

          <Section title="The Charity Checker">
            <p>
              The charity name you search for is sent to our servers to look
              it up in public IRS charity records. The donation-request check
              runs in your browser.
            </p>
          </Section>

          <Section title="Chapter feedback">
            <p>
              At the end of each chapter you can tell us whether it helped and
              leave a comment. This is optional, and we never publish it. Our
              team reads comments to improve the course.
              {partner
                ? ` ${partner.name} may see feedback about this site, with personal details such as emails and phone numbers removed first.`
                : ''}
            </p>
          </Section>

          <Section title="Course progress">
            <p>
              Your progress through the course (which chapters and screens
              you’ve finished) is saved in your browser on this device, so you
              can pick up where you left off. It is not tied to an account.
            </p>
            <p>
              We also count course activity, such as a chapter started or
              finished, using a random session cookie rather than anything
              that identifies you.
              {partner
                ? ` ${partner.name} receives totals only, never records about individual visitors.`
                : ''}{' '}
              You can turn this off on the{' '}
              <Link to="/privacy/opt-out" className={link}>
                opt-out page
              </Link>
              .
            </p>
          </Section>

          <Section title="Cookies and similar technologies">
            <p>
              The site sets a small number of cookies through Google Analytics.
              These cookies do not personally identify you. They only help us
              count and segment visits so we can improve the site.
            </p>
            <p>
              Your browser also stores your course progress and preferences
              (for example, which chapter you’ve completed) locally on your
              own device. A random session cookie lets us count course
              activity, as described above.
            </p>
          </Section>

          <Section title="How to opt out of analytics">
            <p>
              If you would prefer not to be counted in our Google Analytics
              data, you can install Google’s official browser add-on, which
              works on every site that uses Google Analytics:
            </p>
            <p>
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-violet-600 underline decoration-violet-300 underline-offset-2 hover:text-violet-700"
              >
                Google Analytics Opt-out Browser Add-on
              </a>
            </p>
          </Section>

          <Section title="Children">
            <p>
              {siteName} is intended for adults. We do not knowingly collect
              information from children under 13.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about this policy? Email{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="font-medium text-violet-600 underline decoration-violet-300 underline-offset-2 hover:text-violet-700"
              >
                {supportEmail}
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  )
}

/** Arabic privacy policy for the Qatar face; same sections as the English. */
function PrivacyPageArabic() {
  const t = useUi()
  const siteName = QATAR_BRAND.program.ar
  const link = 'font-medium text-violet-600 underline decoration-violet-300 underline-offset-2 hover:text-violet-700'
  return (
    <div className="bg-white">
      <div className="mx-auto max-w-3xl px-6 pb-20 pt-12">
        <p className="text-sm font-medium text-slate-500">{t.privacyEyebrow}</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {t.privacyTitle}
        </h1>
        <p className="mt-3 text-sm text-slate-400">{t.privacyUpdated}</p>

        <div className="mt-10 space-y-8 text-base leading-relaxed text-slate-700">
          <Section title="ما الذي نجمعه">
            <p>
              {siteName} موقع تعليمي. لا نطلب إنشاء حساب، ولا نعرض إعلانات، ولا نبيع معلوماتك
              الشخصية أو نشاركها.
            </p>
            <p>
              نجمع قدراً محدوداً من بيانات الاستخدام المعتادة عبر Google Analytics لنفهم أي الصفحات
              والدروس مفيدة وأين يمكن تحسين الموقع. تشمل هذه البيانات الصفحات التي تزورها، والوقت الذي
              تقضيه في كل صفحة، والدولة والمدينة التي تزور منها، ونوع الجهاز والمتصفح الذي تستخدمه.
            </p>
            <p>
              لا نجمع الأسماء أو العناوين أو أرقام الهواتف أو معلومات الدفع. ولا نستخدم Google Analytics
              لأغراض إعلانية.
            </p>
          </Section>

          <Section title="ملفات تعريف الارتباط والتقنيات المشابهة">
            <p>
              يضع الموقع عدداً قليلاً من ملفات تعريف الارتباط عبر Google Analytics. لا تحدد هذه الملفات
              هويتك شخصياً، بل تساعدنا فقط على عدّ الزيارات وتصنيفها لتحسين الموقع.
            </p>
            <p>
              يحفظ متصفحك أيضاً تقدّمك في الدورة وتفضيلاتك (مثل الفصول التي أكملتها واللغة التي اخترتها)
              محلياً على جهازك. تبقى هذه المعلومات في متصفحك ولا تُرسل إلينا.
            </p>
          </Section>

          <Section title="فاحص الاحتيال">
            <p>
              ما تلصقه أو ترفعه في فاحص الاحتيال يُحلَّل لإعطائك النتيجة فقط. لا يُنشر، ولا يُربط بك،
              ولا يُستخدم للإعلان.
            </p>
          </Section>

          <Section title="كيف تلغي الاشتراك في التحليلات">
            <p>
              إذا كنت تفضّل ألا تُحتسب في بيانات Google Analytics لدينا، يمكنك تثبيت الإضافة الرسمية من
              Google للمتصفح، وهي تعمل على كل موقع يستخدم Google Analytics:
            </p>
            <p>
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className={link}>
                إضافة إلغاء الاشتراك في Google Analytics للمتصفح
              </a>
            </p>
          </Section>

          <Section title="الأطفال">
            <p>{siteName} موجّه للبالغين. لا نجمع عن قصد معلومات من الأطفال دون سن 13 عاماً.</p>
          </Section>

          <Section title="تواصل معنا">
            <p>
              هل لديك أسئلة عن هذه السياسة؟ راسلنا على{' '}
              <a href={`mailto:${QATAR_BRAND.fraudEmail}`} className={link} dir="ltr">
                {QATAR_BRAND.fraudEmail}
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <h2 className="text-xl font-semibold tracking-tight text-slate-900">
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  )
}
